import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AnalyticsDashboard from './AnalyticsDashboard';
import { eventsAPI } from '../../../../services/api';
import { useAlert } from '../../../../contexts/AlertContext';
import { useAuthStore } from '../../../../store/authStore';
import {
    DollarSign,
    Tent,
    Activity,
    Target,
    Plus,
    BarChart3,
    Edit2,
    Trash2,
    CheckCircle,
    Calendar
} from 'lucide-react';
import './EventManagementPanel.css';

const EventManagementPanel = () => {
    const { showAlert } = useAlert();
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [events, setEvents] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [systemStats, setSystemStats] = useState(null);

    // Filter states
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const roleFlags = {
        isAdmin: user?.isAdmin || user?.admin,
        isPlanner: user?.isPlanner || user?.planner,
        isPromoter: user?.isPromoter || user?.promoter
    };

    useEffect(() => {
        if (user && (roleFlags.isAdmin || roleFlags.isPlanner || roleFlags.isPromoter)) {
            loadEvents(user, roleFlags);
            loadSystemStats();
        }
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps


    const loadEvents = async (currentUser, flags = roleFlags) => {
        try {
            setLoading(true);
            let page;
            if (flags.isAdmin) {
                page = await eventsAPI.getAll({ size: 1000, sort: 'startTime,desc' });
            } else if (flags.isPlanner) {
                page = await eventsAPI.getByPlanner(currentUser.id);
            } else if (flags.isPromoter) {
                page = await eventsAPI.getByPromoter(currentUser.id);
            } else {
                page = [];
            }
            const eventsData = Array.isArray(page) ? page : (page?.content || []);

            // Fetch analytics for admins/planners to enrich events
            let analyticsByEvent = {};
            if (flags.isAdmin || flags.isPlanner) {
                try {
                    const analytics = await eventsAPI.getAnalytics();
                    const analyticsData = analytics?.data ?? analytics;
                    (analyticsData?.events || []).forEach((evt) => {
                        analyticsByEvent[evt.eventId] = {
                            revenue: Number(evt.revenue) || 0,
                            ticketsSold: Number(evt.ticketsSold) || 0,
                            occupancy: typeof evt.occupancy === 'number' ? evt.occupancy : null,
                            capacity: evt.capacity != null ? Number(evt.capacity) : null
                        };
                    });
                } catch (err) {
                    console.warn('Event analytics unavailable:', err);
                }
            }

            // Enhance events with analytics (if available)
            const enhancedEvents = await Promise.all(
                eventsData.map(async (event) => {
                    const analytics = analyticsByEvent[event.id] || {};
                    return {
                        ...event,
                        analytics: {
                            totalRevenue: analytics.revenue ?? 0,
                            ticketsSold: analytics.ticketsSold ?? 0,
                            occupancyRate: analytics.occupancy ?? 0,
                            capacity: analytics.capacity ?? null
                        },
                        status: getEventStatus(event)
                    };
                })
            );

            setEvents(enhancedEvents);
        } catch (error) {
            console.error('Error loading events:', error);
            setEvents([]);
        } finally {
            setLoading(false);
        }
    };

    const loadSystemStats = async () => {
        try {
            const flags = roleFlags;
            if (!flags.isAdmin && !flags.isPlanner) {
                setSystemStats(null);
                return;
            }

            const analytics = await eventsAPI.getAnalytics();
            const analyticsData = analytics?.data ?? analytics;
            const stats = {
                totalEvents: analyticsData?.totalEvents || 0,
                activeEvents: analyticsData?.activeEvents || 0,
                totalRevenue: analyticsData?.totalRevenue || 0,
                averageOccupancy: analyticsData?.averageOccupancy
                    ? Number(analyticsData.averageOccupancy.toFixed(2))
                    : 0,
                ticketsSold: (analyticsData?.events || []).reduce((sum, e) => sum + (Number(e.ticketsSold) || 0), 0)
            };
            setSystemStats(stats);
        } catch (error) {
            console.error('Error loading system stats:', error);
            setSystemStats(null);
        }
    };

    const getEventStatus = (event) => {
        const now = new Date();
        const eventDate = new Date(event.startTime);

        if (eventDate < now) {
            return 'completed';
        } else if (eventDate.getTime() - now.getTime() < 24 * 60 * 60 * 1000) {
            return 'live';
        } else {
            return 'upcoming';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'live': return <span className="status-with-icon"><Activity size={12} className="live-icon" /> Live</span>;
            case 'upcoming': return <span className="status-with-icon"><Calendar size={12} /> Upcoming</span>;
            case 'completed': return <span className="status-with-icon"><CheckCircle size={12} /> Completed</span>;
            default: return 'Unknown';
        }
    };

    const filterEvents = useCallback(() => {
        let filtered = [...events];

        if (statusFilter !== 'all') {
            filtered = filtered.filter(event => event.status === statusFilter);
        }

        if (categoryFilter !== 'all') {
            filtered = filtered.filter(event => event.category === categoryFilter);
        }

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(event =>
                event.title.toLowerCase().includes(query) ||
                event.description?.toLowerCase().includes(query) ||
                event.venue?.name?.toLowerCase().includes(query)
            );
        }

        setFilteredEvents(filtered);
    }, [events, statusFilter, categoryFilter, searchQuery]);

    useEffect(() => {
        filterEvents();
    }, [filterEvents]);

    const handleEventAction = async (event, action) => {
        switch (action) {
            case 'view-analytics':
                setSelectedEvent(event);
                setShowAnalytics(true);
                break;
            case 'edit':
                // Navigate to edit page (wizard handles both create and edit)
                navigate(`/planner/events/${event.id}`);
                break;
            case 'delete':
                showAlert({
                    title: 'Delete event?',
                    message: `Are you sure you want to delete "${event.title}"? This cannot be undone.`,
                    primaryLabel: 'Delete',
                    secondaryLabel: 'Cancel',
                    onPrimary: async () => {
                        try {
                            await eventsAPI.delete(event.id);
                            loadEvents();
                        } catch (error) {
                            console.error('Error deleting event:', error);
                            const errorMsg = error.response?.data?.message || error.message || 'Failed to delete event. Please try again.';
                            showAlert({
                                title: 'Delete failed',
                                message: errorMsg
                            });
                        }
                    }
                });
                break;
            default:
                break;
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="admin-events-panel">
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '320px',
                    fontSize: '1.05rem',
                    color: 'rgba(255, 255, 255, 0.5)'
                }}>
                    Loading events...
                </div>
            </div>
        );
    }

    return (
        <div className="admin-events-panel">
            {/* System Stats */}
            {systemStats && (
                <div className="events-stats-grid">
                    <div className="events-stat-card" style={{ borderLeftColor: '#10b981' }}>
                        <div className="stat-header">
                            <span className="stat-icon"><DollarSign size={24} color="#10b981" /></span>
                            <span className="stat-title">Total Revenue</span>
                        </div>
                        <div className="stat-value">
                            {formatCurrency(systemStats.totalRevenue)}
                        </div>
                        <div className="stat-change">+12.5%</div>
                    </div>

                    <div className="events-stat-card" style={{ borderLeftColor: '#3b82f6' }}>
                        <div className="stat-header">
                            <span className="stat-icon"><Tent size={24} color="#3b82f6" /></span>
                            <span className="stat-title">Total Events</span>
                        </div>
                        <div className="stat-value">{systemStats.totalEvents}</div>
                        <div className="stat-change">+5.2%</div>
                    </div>

                    <div className="events-stat-card" style={{ borderLeftColor: '#f59e0b' }}>
                        <div className="stat-header">
                            <span className="stat-icon"><Activity size={24} color="#f59e0b" /></span>
                            <span className="stat-title">Active Events</span>
                        </div>
                        <div className="stat-value">{systemStats.activeEvents}</div>
                        <div className="stat-change">Active</div>
                    </div>

                    <div className="events-stat-card" style={{ borderLeftColor: '#8b5cf6' }}>
                        <div className="stat-header">
                            <span className="stat-icon"><Target size={24} color="#8b5cf6" /></span>
                            <span className="stat-title">Avg Occupancy</span>
                        </div>
                        <div className="stat-value">{systemStats.averageOccupancy}%</div>
                        <div className="stat-change">+3.1%</div>
                    </div>
                </div>
            )}

            {/* Filter Bar */}
            <div className="events-filter-bar">
                <input
                    className="events-search-input"
                    type="text"
                    placeholder="Search events by title, description, or venue..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />

                <div className="events-filter-group">
                    <label>Status</label>
                    <select
                        className="events-filter-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All Status</option>
                        <option value="upcoming">Upcoming</option>
                        <option value="live">Live</option>
                        <option value="completed">Completed</option>
                    </select>
                </div>

                <div className="events-filter-group">
                    <label>Category</label>
                    <select
                        className="events-filter-select"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                        <option value="all">All Categories</option>
                        <option value="CONCERT">Concert</option>
                        <option value="THEATER">Theater</option>
                        <option value="SPORTS">Sports</option>
                        <option value="COMEDY">Comedy</option>
                        <option value="OTHER">Other</option>
                    </select>
                </div>

                <button
                    className="events-action-button"
                    data-variant="primary"
                    type="button"
                    onClick={() => window.location.href = '/admin/events/create'}
                >
                    <Plus size={16} /> Create Event
                </button>
            </div>

            {/* Events Grid */}
            <div className="events-card-grid">
                {filteredEvents.map((event) => (
                    <div className="events-card" key={event.id}>
                        <div className="events-card-header">
                            <div className="event-title">{event.title}</div>
                            <div className="event-meta">
                                <div className="event-date">
                                    {formatDate(event.startTime)}
                                </div>
                                <div className="event-status">
                                    {getStatusLabel(event.status)}
                                </div>
                            </div>
                        </div>

                        <div className="events-card-body">
                            <div className="event-description">
                                {event.description?.substring(0, 120)}
                                {event.description?.length > 120 && '...'}
                            </div>

                            <div className="events-metrics-grid">
                                <div className="event-metric">
                                    <div className="metric-value">
                                        {formatCurrency(event.analytics?.totalRevenue || 0)}
                                    </div>
                                    <div className="metric-label">Revenue</div>
                                </div>
                                <div className="event-metric">
                                    <div className="metric-value">
                                        {event.analytics?.ticketsSold || 0}
                                    </div>
                                    <div className="metric-label">Sold</div>
                                </div>
                                <div className="event-metric">
                                    <div className="metric-value">
                                        {event.analytics?.occupancyRate || 0}%
                                    </div>
                                    <div className="metric-label">Occupied</div>
                                </div>
                            </div>

                            <div className="events-action-bar">
                                <button
                                    className="events-action-button"
                                    data-variant="primary"
                                    type="button"
                                    onClick={() => handleEventAction(event, 'view-analytics')}
                                >
                                    <BarChart3 size={14} /> Analytics
                                </button>
                                <button
                                    className="events-action-button"
                                    data-variant="secondary"
                                    type="button"
                                    onClick={() => handleEventAction(event, 'edit')}
                                >
                                    <Edit2 size={14} /> Edit
                                </button>
                                <button
                                    className="events-action-button"
                                    data-variant="danger"
                                    type="button"
                                    onClick={() => handleEventAction(event, 'delete')}
                                >
                                    <Trash2 size={14} /> Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Analytics Modal */}
            {showAnalytics && selectedEvent && (
                <div className="events-modal-overlay" onClick={() => setShowAnalytics(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2><BarChart3 size={20} /> Analytics: {selectedEvent.title}</h2>
                        </div>
                        <div className="modal-body">
                            <AnalyticsDashboard
                                eventId={selectedEvent.id}
                                refreshInterval={30000}
                                showControls={true}
                            />
                        </div>
                        <div className="modal-footer">
                            <button
                                className="events-action-button"
                                data-variant="secondary"
                                type="button"
                                onClick={() => setShowAnalytics(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {filteredEvents.length === 0 && !loading && (
                <div style={{
                    textAlign: 'center',
                    padding: '4rem',
                    color: '#6b7280'
                }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}><Tent size={48} /></div>
                    <h3>No events found</h3>
                    <p>
                        {searchQuery || statusFilter !== 'all' || categoryFilter !== 'all'
                            ? 'Try adjusting your filters or search terms.'
                            : 'Create your first event to get started!'}
                    </p>
                    <button
                        className="events-action-button"
                        data-variant="primary"
                        type="button"
                        onClick={() => window.location.href = '/admin/events/create'}
                        style={{ marginTop: '1rem' }}
                    >
                        Create First Event
                    </button>
                </div>
            )}
        </div>
    );
};

export default EventManagementPanel; 
